import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Box, Typography } from '@mui/material';
import PnlText from '../../design/PnlText';
import { formatCurrencyInt } from '../../design/format';
import { pnlColors } from '../../design/theme';
import { TraderOverviewRow } from '../../hooks/useHomeData';
import { PerformanceHistoryItem } from '../../hooks/useStatistics';

interface TraderCardProps {
  row: TraderOverviewRow;
  spark?: PerformanceHistoryItem[];
}

const labelSx = {
  fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary',
  textTransform: 'uppercase', letterSpacing: '.05em',
} as const;

const SPARK_W = 240;
const SPARK_H = 42;
const SPARK_PAD = 2; // half the 2px stroke plus a hair, so the line never clips at the viewBox edge

// Build stroke + area SVG paths from the cumulative P&L series. Returns null when
// there aren't enough points to draw a line (the card renders no sparkline then).
const buildSparkPaths = (spark?: PerformanceHistoryItem[]): { line: string; area: string; positive: boolean } | null => {
  if (!spark || spark.length < 2) return null;
  const values = spark.map((p) => p.cumulative_pnl);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1; // avoid divide-by-zero on a flat series
  const stepX = SPARK_W / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    // Invert Y: SVG origin is top-left, higher P&L should sit higher up.
    const y = SPARK_H - SPARK_PAD - ((v - min) / range) * (SPARK_H - 2 * SPARK_PAD);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const line = `M${points.join(' L')}`;
  const area = `${line} L${SPARK_W},${SPARK_H} L0,${SPARK_H} Z`;
  return { line, area, positive: values[values.length - 1] >= 0 };
};

/**
 * Rich, read-only per-trader summary card. The whole card drills into the trader.
 * Sparkline is a plain SVG (no axes/dots/tooltip) so it renders at any width and in tests.
 */
const TraderCard: React.FC<TraderCardProps> = ({ row, spark }) => {
  const navigate = useNavigate();
  const paths = useMemo(() => buildSparkPaths(spark), [spark]);
  const sparkColor = paths?.positive ? pnlColors.gain : pnlColors.loss;

  // Overall return against what was put in. Omitted entirely (no placeholder)
  // when either side is missing, since a percentage of nothing means nothing.
  const { net_liquid_assets: nla, deposits } = row;
  const overallPnlPercent =
    nla !== null && deposits !== null && deposits > 0
      ? ((nla - deposits) / deposits) * 100
      : null;

  const go = () => navigate('/trader/' + row.trader_id);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Activate on Enter/Space like a native button; preventDefault on Space
    // stops the page from scrolling.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      go();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={handleKeyDown}
      sx={{
        p: 2.5,
        cursor: 'pointer',
        transition: 'transform .2s, box-shadow .2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{row.name}</Typography>
      </Box>

      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: 'primary.dark', mb: 1 }}>
        {row.net_liquid_assets === null ? 'N/A' : formatCurrencyInt(row.net_liquid_assets)}
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
        <Box>
          <Typography sx={labelSx}>Realized</Typography>
          <PnlText value={row.realized_pnl} sx={{ fontSize: '0.9rem' }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Unrealized</Typography>
          <PnlText value={row.unrealized_pnl} sx={{ fontSize: '0.9rem' }} />
        </Box>
        {overallPnlPercent !== null && (
          <Box>
            <Typography sx={labelSx}>P&amp;L %</Typography>
            <PnlText value={overallPnlPercent} format="percent" sx={{ fontSize: '0.9rem' }} />
          </Box>
        )}
      </Box>

      <Box sx={{ height: SPARK_H, mb: 1.5 }}>
        {paths && (
          <svg
            width="100%"
            height={SPARK_H}
            viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d={paths.area} fill={sparkColor} opacity={0.08} />
            <path d={paths.line} fill="none" stroke={sparkColor} strokeWidth={2} />
          </svg>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: '0.78rem', color: 'text.secondary',
          borderTop: '1px solid', borderColor: 'divider', pt: 1.5,
        }}
      >
        <Typography variant="inherit" component="span">
          Win <Box component="b" sx={{ color: 'text.primary' }}>{row.win_rate.toFixed(1)}%</Box>
        </Typography>
        <Typography variant="inherit" component="span">
          <Box component="b" sx={{ color: 'text.primary' }}>{row.total_trades}</Box> trades
        </Typography>
        <Typography variant="inherit" component="span">
          <Box component="b" sx={{ color: 'text.primary' }}>{row.open_positions}</Box> open
        </Typography>
      </Box>
    </Card>
  );
};

export default TraderCard;
